import React, { Component } from 'react';

import { Modal } from 'bloomer/lib/components/Modal/Modal';
import { ModalBackground } from 'bloomer/lib/components/Modal/ModalBackground';
import { ModalCard } from 'bloomer/lib/components/Modal/Card/ModalCard';
import { ModalCardHeader } from 'bloomer/lib/components/Modal/Card/ModalCardHeader';
import { ModalCardTitle } from 'bloomer/lib/components/Modal/Card/ModalCardTitle';
import { Delete } from 'bloomer/lib/elements/Delete';
import { ModalCardBody } from 'bloomer/lib/components/Modal/Card/ModalCardBody';
import { ModalCardFooter } from 'bloomer/lib/components/Modal/Card/ModalCardFooter';
import { Button } from 'bloomer/lib/elements/Button';
import Conditional from './Conditional';
import Animal from '../Entities/Animal';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Input } from 'bloomer/lib/elements/Form/Input';
import RadioButtonFieldSet from '../elements/RadioButtonFieldSet';
import { Help } from 'bloomer/lib/elements/Form/Help';

interface PropTypes {
  animal: Animal;
  handleClose: () => void;
  handleSubmit: (a: any) => void;
}

interface StatyeTypes {
  time: 'AM' | 'PM' | '';
  initial: string;
  appetiteDry: 'all' | '3/4' | '1/2' | '1/4' | 'nibbling' | 'none' | '';
  appetiteWet: 'all' | '3/4' | '1/2' | '1/4' | 'nibbling' | 'none' | '';
  drinking: 'normal' | 'a lot' | 'very little' | 'spilled bowl' | 'none' | '';
  vomit: 'food' | 'bile' | 'blood' | 'none' | '';
  stool: '1' | '2' | '3' | '4' | '5' | '6' | '7' | 'none' | '';
  urine: 'normal' | 'excessive' | 'strong odor' | 'bloody' | 'straining' | 'none' | '';
  cough: 'yes' | 'no' | '';
  sneeze: 'yes' | 'no' | '';
  error: string;
}

class EvaluationModal extends Component<PropTypes, StatyeTypes> {
  constructor(props: PropTypes) {
    super(props);
    this.modalRef = React.createRef();
    this.errorRef = React.createRef();
  }
  modalRef: React.RefObject<any>;
  errorRef: React.RefObject<any>;
  state: StatyeTypes = {
    initial: '',
    time: '',
    appetiteDry: '',
    appetiteWet: '',
    drinking: '',
    vomit: '',
    stool: '',
    urine: '',
    cough: '',
    sneeze: '',
    error: '',
  };

  handleChange = (e: React.FormEvent<HTMLElement>) => {
    const name = (e.target as HTMLInputElement).name as keyof StatyeTypes;
    const value = (e.target as HTMLInputElement).value as StatyeTypes[keyof StatyeTypes];

    if (!name) return;

    this.setState({
      [name]: value,
    } as any);
  };

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.setState({
      error: '',
    });
    const { animal } = this.props;

    const evaluation = { ...this.state, aNumber: animal.aNumber };
    Reflect.deleteProperty(evaluation, 'error');

    const emptyInputs = Object.entries(evaluation).filter(([key, val]) => val.toString().trim() === ''); // eslint-disable-line @typescript-eslint/no-unused-vars

    if (emptyInputs.length) {
      const first = emptyInputs[0];
      this.setState(
        {
          error: first[0],
        },
        this.scrollToError,
      );
      return;
    }

    this.props.handleSubmit(evaluation);
  };

  scrollToError = () => {
    const modal = document.querySelector('.modal-card-body');
    if (!modal) return;
    modal.scrollTop = this.errorRef.current.offsetTop;
  };

  render() {
    const { animal, handleClose } = this.props;
    const { time, initial, appetiteDry, drinking, vomit, stool, urine, cough, sneeze, appetiteWet, error } = this.state;

    const initialRef = 'initial' === error ? this.errorRef : null;
    return (
      <Modal isActive={true}>
        <ModalBackground />
        <ModalCard>
          <ModalCardHeader>
            <ModalCardTitle>{animal.name} Evaluation</ModalCardTitle>
            <Delete onClick={handleClose} />
          </ModalCardHeader>
          <ModalCardBody>
            <form onSubmit={this.handleSubmit} ref={this.modalRef}>
              <RadioButtonFieldSet
                label="Time of Evaluation"
                name="time"
                value={time as string}
                radioButtons={['AM', 'PM']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <Field>
                <Label htmlFor="initial">Inital / Name</Label>
                <Control>
                  <Input
                    id="initial"
                    name="initial"
                    type="text"
                    value={initial}
                    onChange={this.handleChange}
                    isColor={initialRef !== null ? 'danger' : ''}
                  />
                  <span ref={initialRef} aria-hidden="true" />
                </Control>
                <Conditional check={initialRef !== null}>
                  <Help isColor="danger">Please fill out this field</Help>
                </Conditional>
              </Field>
              <RadioButtonFieldSet
                label="Appetite - DRY"
                name="appetiteDry"
                value={appetiteDry as string}
                radioButtons={['all', '3/4', '1/2', '1/4', 'nibbling', 'none']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Appetite - WET"
                name="appetiteWet"
                value={appetiteWet as string}
                radioButtons={['all', '3/4', '1/2', '1/4', 'nibbling', 'none']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Drinking"
                name="drinking"
                value={drinking as string}
                radioButtons={['normal', 'a lot', 'very little', 'spilled bowl', 'none']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Stools"
                name="stool"
                value={stool as string}
                radioButtons={['1', '2', '3', '4', '5', '6', '7', 'none']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Urine"
                name="urine"
                value={urine as string}
                radioButtons={['normal', 'excessive', 'strong odor', 'bloody', 'straining', 'none']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Vomiting"
                name="vomit"
                value={vomit as string}
                radioButtons={['food', 'bile', 'blood', 'none']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Coughing"
                name="cough"
                value={cough as string}
                radioButtons={['yes', 'no']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <RadioButtonFieldSet
                label="Sneezing"
                name="sneeze"
                value={sneeze as string}
                radioButtons={['yes', 'no']}
                handleChange={this.handleChange}
                required={false}
                errRef={this.errorRef}
                error={error}
              />
              <Field>
                <Control>
                  <Button type="submit" isColor="primary">
                    Save
                  </Button>
                </Control>
              </Field>
            </form>
          </ModalCardBody>
          <ModalCardFooter />
        </ModalCard>
      </Modal>
    );
  }
}
export default EvaluationModal;
